/**
 * Read the member's enrolment status once per session, ahead of their first
 * write — so that the sealing hook, and `pause` in particular, can decide from
 * what the device already knows.
 *
 * WHY. The hook reads the status lazily, the first time a write is sent (see
 * `sealed-writes.js#apiSaysEnrolled`). For a member without a key — every
 * member while `courses-e2ee` is off — nothing else reads it: the launch read
 * is for key holders. So the first save of a session carried a status
 * round-trip on its critical path, and a `pause` before that first send had to
 * make a request inside the background window the app is about to lose. Read
 * on entering Next Steps (and the Me tab, which needs it for its devices row),
 * both decide locally.
 *
 * Never awaited: a page must not wait on it, and a hook that finds the status
 * unread still reads for itself with its own backoff. One attempt per session:
 * a read the action has already recorded as failed is not repeated on every
 * page entry while the network is down — the hook's read is the retry.
 */

/**
 * Whether the devices snapshot has never landed. `=== null`, not `== null`: the
 * module's initial `lastRefreshedAt` is `null`, and a store without the module
 * reads `undefined` — dispatching into that is a Vuex console error for a
 * request with nowhere to go.
 *
 * @param {object} store
 * @returns {boolean}
 */
export function statusNeverRead(store) {
  return store?.state?.['courses-devices']?.lastRefreshedAt === null;
}

/**
 * @param {object} store the root Vuex store
 * @returns {boolean} whether a read was started
 */
export function primeEnrolmentStatus(store) {
  if (!statusNeverRead(store) || store.state['courses-devices'].readError) return false;
  try {
    // `Promise.resolve`: a spec's fake dispatch may return nothing, or throw.
    Promise.resolve(store.dispatch('courses-devices/refreshStatus')).catch(() => {});
  } catch (error) {
    /* Recorded in the module's `readError` by the action itself. */
  }
  return true;
}
